/**
 * Leitura dos TEXTOS da planilha de pedidos (Werc 2.0 — F2). Usado pelo
 * importador e pelo formulário. Regra de ouro: nunca descartar o original — se
 * algo não for reconhecido, o campo estruturado fica vazio e o texto continua.
 */

/** Nome comparável: sem acento, minúsculas, espaços únicos, sem pontuação nas pontas. */
export function normalizarNome(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/^[\s.,;:-]+|[\s.,;:-]+$/g, '')
    .trim()
}

/** Produto comparável: maiúsculas, espaços únicos, "586x920"/"586×920" → "586X920", "b-flap" → "B-FLAP". */
export function normalizarProduto(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()
    .replace(/(\d)\s*[xX×]\s*(\d)/g, '$1X$2') // "470×610" (sinal de vezes) também
    .replace(/\s+/g, ' ')
    .replace(/^[\s.,;:-]+|[\s.,;:-]+$/g, '')
    .trim()
}

const dois = (n: number) => String(n).padStart(2, '0')

/** "07.01.2026" | "07/01/2026" | "7.1.26" → "2026-01-07"; inválido → null. */
export function dataBRparaISO(s: string): string | null {
  const m = /(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})(?!\d)/.exec(s)
  if (!m) return null
  const dia = Number(m[1])
  const mes = Number(m[2])
  let ano = Number(m[3])
  if (m[3].length === 2) ano += 2000
  if (m[3].length === 3) return null // ex.: "09.01.206" (digitação) — fica só o texto
  if (mes < 1 || mes > 12 || dia < 1 || dia > 31 || ano < 2000 || ano > 2100) return null
  const d = new Date(Date.UTC(ano, mes - 1, dia))
  if (d.getUTCDate() !== dia) return null // 31/02 etc.
  return `${ano}-${dois(mes)}-${dois(dia)}`
}

/** Date do Excel (meia-noite UTC) → "aaaa-mm-dd". */
export function dataExcelParaISO(d: Date): string {
  return `${d.getUTCFullYear()}-${dois(d.getUTCMonth() + 1)}-${dois(d.getUTCDate())}`
}

/** "2026-01-07" → "07.01.2026" (formato da planilha). */
export function isoParaBR(iso: string | null | undefined): string {
  if (!iso) return ''
  const [a, m, d] = iso.split('-')
  return `${d}.${m}.${a}`
}

/** C: "4504358192 - 07.01.2026" → { num: "4504358192", data: "2026-01-07" }. */
export function lerPedidoTexto(s: string): { num: string | null; data: string | null } {
  const t = s.trim()
  const data = dataBRparaISO(t)
  // número = o que vem antes do " - <data>" (ou o texto inteiro se não houver data)
  const m = /^(.*?)\s*-\s*\d{1,2}[./-]\d{1,2}[./-]\d{2,4}\s*$/.exec(t)
  // só data, sem número (ex.: "28.01.2026") → num nulo (achado na importação real)
  const num = (m ? m[1] : data ? '' : t).trim() || null
  return { num, data }
}

/** G: "09.02.2026 - OK!" → { data, ok: true }; "10/11/26" → { data, ok: false }. */
export function lerEntregou(s: string): { data: string | null; ok: boolean } {
  return { data: dataBRparaISO(s), ok: /\bok\b/i.test(s) }
}

export interface ItemLido {
  quantidade: number | null
  produto: string | null // texto do produto (sem a quantidade e sem o complemento)
  complemento: string | null
  descricao: string // trecho original
}

/**
 * N: "26x VMP F 490X590 / 20x VMP F 300X1000 + G1" → itens.
 * Aceita "26x", "26 x", "26X", "(1)vmp" (2020) e item sem quantidade ("Service").
 * " + G1", "( G1 )", "c/ silicone" viram complemento do item.
 */
export function lerItens(texto: string): ItemLido[] {
  const partes = texto
    .split(/\s*\/\s*(?=\(?\d+\)?\s*[xX]?\s*[A-Za-z(]|[A-Za-z])/)
    .map((p) => p.trim())
    .filter(Boolean)
  return partes.map((descricao) => {
    let resto = descricao
    let quantidade: number | null = null
    const q = /^\(?\s*(\d+(?:[.,]\d+)?)\s*\)?\s*[xX]?\s*(?=\S)/.exec(resto)
    // só é quantidade se houver "x" ou parênteses (evita ler "1130X1130" como quantidade)
    if (q && (/^\(/.test(resto) || /^\d+(?:[.,]\d+)?\s*[xX]\s*\S/.test(resto))) {
      quantidade = Number(q[1].replace(',', '.'))
      resto = resto.slice(q[0].length)
    }
    let complemento: string | null = null
    const c = /\s*(\+\s*\S.*|\(\s*G\d+\s*\)|c\/\s*\S.*)$/i.exec(resto)
    if (c && c.index > 0) {
      complemento = c[1].trim()
      resto = resto.slice(0, c.index)
    }
    const produto = resto.trim() || null
    return { quantidade, produto, complemento, descricao }
  })
}
